import { useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import { Flex, cssVar, Box, Button, Text, Popover, PopoverContent, PopoverTrigger } from '@cad-arc';
import {
    TransportForm,
    ConsolidatedLocationView,
    HospitalTransportFormView,
} from '@mark43/cad-api';

import { useIntegratedForm } from '../../../forms/use-integrated-form';
import { LocationSelectValueDisplay } from '../../../location-select/components/location-select-value-display';
import { HospitalTransportLocationSelectOption } from './hospital-transport-location-select-option';

export function HospitalTransportLocation({
    isSubmitInProgress,
    showHospitalLocationRequiredErrorMessage,
    setShowHospitalLocationRequiredErrorMessage,
}: {
    isSubmitInProgress: boolean;
    showHospitalLocationRequiredErrorMessage: boolean;
    setShowHospitalLocationRequiredErrorMessage: Dispatch<SetStateAction<boolean>>;
}) {
    const { form } = useIntegratedForm<TransportForm>();
    const hospitals = form.watch('hospitals');
    const hospitalId = form.watch('hospitalId');

    const selectedTransportHospital = useMemo(() => {
        if (!hospitalId) {
            return;
        }

        return hospitals?.find((hospital: HospitalTransportFormView) => hospital.id === hospitalId);
    }, [hospitalId, hospitals]);

    useEffect(() => {
        setShowHospitalLocationRequiredErrorMessage(false);
    }, [hospitalId, setShowHospitalLocationRequiredErrorMessage]);

    useEffect(() => {
        if (!selectedTransportHospital) {
            return;
        }

        form.setValue(
            'consolidatedLocationView',
            selectedTransportHospital.location as ConsolidatedLocationView
        );
    }, [selectedTransportHospital, form]);

    const removeHospital = () => {
        form.setValue('hospitalId', undefined);
    };

    return (
        <Box>
            {selectedTransportHospital ? (
                <LocationSelectValueDisplay
                    locationTitle={selectedTransportHospital?.name}
                    value={selectedTransportHospital?.location}
                    onDelete={removeHospital}
                    isDisabled={isSubmitInProgress}
                />
            ) : (
                <Popover hasArrow={false}>
                    <PopoverTrigger asChild>
                        <Button
                            disabled={isSubmitInProgress}
                            leadingVisual="Add"
                            size="lg"
                            onClick={() => setShowHospitalLocationRequiredErrorMessage(false)}
                            style={{
                                width: '100%',
                                ...(showHospitalLocationRequiredErrorMessage
                                    ? {
                                          border: `2px solid ${cssVar(
                                              'arc.colors.negative.default'
                                          )}`,
                                      }
                                    : {}),
                            }}
                        >
                            Hospital Location
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent>
                        <Flex
                            width="380px"
                            flexDirection="column"
                            alignItems="center"
                            maxHeight="400px"
                            overflowY="auto"
                            padding={0}
                            margin={0}
                        >
                            {hospitals?.map((hospital: HospitalTransportFormView) => (
                                <HospitalTransportLocationSelectOption
                                    key={hospital.id}
                                    hospitals={hospitals}
                                    hospitalId={hospital.id}
                                />
                            ))}
                        </Flex>
                    </PopoverContent>
                    {showHospitalLocationRequiredErrorMessage && (
                        <Text fontSize="sm" color="negative">
                            Required
                        </Text>
                    )}
                </Popover>
            )}
        </Box>
    );
}
